import { ZONE_ID } from '../../../constants/importedShapefiles';
import { inverseCoordinates } from '../../geo';
import { IMPORTED_SHAPEFILE_PROPERTY_COLOR } from '../../../constants/colors';

function extractShapefile(event) {
  const { meta, payload, eventDate, fieldId } = event;
  const [shapefile] = payload;
  return {
    filename: meta.name,
    shapefileUri: shapefile.uri,
    northeastPoint: shapefile.meta.bbox.northeast.coordinates.reverse(),
    southwestPoint: shapefile.meta.bbox.southwest.coordinates.reverse(),
    eventDate,
    type: meta['content-type'],
    id: meta.id,
    uuid: shapefile.uuid,
    fieldId,
  };
}

export function extractImportedShapefiles(shapefiles) {
  return {
    importedShapefiles: shapefiles.map(extractShapefile),
  };
}

/**
 * Get the coordinates and inverse them
 * @param {object} feature
 * @return {array} inversed coordinates
 * */
export function getCoordinates(feature) {
  return inverseCoordinates(
    feature.geometry.coordinates
      ? feature.geometry.coordinates
      : feature.geometry.geometries.map((o) => o.coordinates)
  );
}

/**
 * Extract importedshapefile properties and metadata info
 * @param {String} propertyName
 * @param {Array} [features=[]]
 * @returns {Array}
 * Walk every geoJSON Feature and check if this property is available for that
 * feature. If it is then get the value for that property in the feature and add
 * it and its cooresponding geometries to a dictionary.
 * The end result should be a dictionary that contains a list of unique values for
 * this specific property as keys and an array of geometries as the values.
 * */
export function extractGeometries(propertyName, features) {
  const color = IMPORTED_SHAPEFILE_PROPERTY_COLOR;

  const properties = features.reduce((_properties, feature) => {
    const value = feature.properties[propertyName];
    if (value === undefined) {
      return _properties;
    }

    const newCoordinates = getCoordinates(feature);
    const coordinates = _properties[value]
      ? newCoordinates.concat(_properties[value].coordinates)
      : newCoordinates;

    return {
      ..._properties,
      [value]: { value, coordinates, color },
    };
  }, {});

  return Object.values(properties);
}

/** Extract Unique Property List
 * @param {Array} features
 * @return {Set}
 */
export function getUniquePropertyList(features) {
  // Create a unique list of properties for this event
  const uniquePropertyNamesSet = new Set();
  features.forEach(({ properties = {} }) => {
    Object.keys(properties).forEach((property) => {
      uniquePropertyNamesSet.add(property);
    });
  });

  uniquePropertyNamesSet.delete(ZONE_ID);

  return uniquePropertyNamesSet;
}

/**
 * Compare 2 Imported Shapefile Properties
 * @param {Object} property1
 * @param {Object} property2
 * @return {Array}
 */
export function compareImportedShapefileProperties(property1, property2) {
  if (property1.name > property2.name) {
    return 1;
  }
  if (property2.name > property1.name) {
    return -1;
  }
  return 0;
}

/** Configure the Properties
 * @param {Set} uniqueProperties
 * @param {Array} features
 * @returns {Array}
 */
export function configureProperties(uniqueProperties, features) {
  return Array.from(uniqueProperties).map((propName) => ({
    name: propName,
    geometries: extractGeometries(propName, features),
  }));
}

/**
 * Extract imported shapefile properties and metadata info
 * @param {Array} features
 * @return {Array}
 */
export function extractImportedShapefileProperties(features = []) {
  const propertyList = getUniquePropertyList(features);
  const configuredProperties = configureProperties(propertyList, features);
  return configuredProperties.sort(compareImportedShapefileProperties);
}
